/**
 * CypAdhdObservationTaskPanel — one Clinical Observation task of the CYP ADHD
 * Semi-Structured Interview, shown only when cypAdhdFlow.mse ===
 * 'observationInSsi'. No live org stamps that today; the panel stays so the
 * org document can turn the phase back on. See
 * config/cypAdhdObservationConfig.js for what the phase is and why it exists.
 *
 * SHAPE. The adult autism Behavioural Observation's Task Administration
 * (ObservationTaskPanelV2a) in miniature: a heading with a "Not administered"
 * switch, the prompts the clinician works through on the left, and ONE notes
 * box on the right whose notes can be tagged. Nothing else — no structured
 * observation chips, no evidence ratings, no criterion table. Helen asked for
 * "nowhere near as involved as an ADOS", and the notes are what the symptom
 * pages read.
 *
 * CHROME. The card is CypAdhdSsiDomainPanel's (a "Task N of M" counter, a
 * white card holding a gray-50 rounded-2xl body, prompts left / notes right
 * split by a hairline), so the observation reads as the first phase of the
 * same interview rather than a module bolted on the front. The switch and
 * the not-administered banner are the autism panel's markup verbatim, so a
 * clinician who runs both pathways meets one control.
 *
 * PROMPTS are a plain checklist, not the autism `questionnaire` field type:
 * that type is bound to ObservationTaskPanelV2a's drag-into-notes and
 * marked-as-asked internals, which this panel does not want. A tick here
 * means "I did this" and is stored as promptsDone so the clinician can see
 * where they got to if the child's attention (or the appointment) runs out.
 *
 * NOT ADMINISTERED locks the panel the same way preview / unassigned does:
 * `readOnly` is derived from the flag, so every edit guard inherits it.
 * Notes are kept, greyed, and excluded from the symptom pages
 * (collectCypObservationTaggedNotes skips the task). Unmarking restores them.
 * Marking a task that already holds notes asks first, because the clinician
 * is about to hide evidence from the symptom pages; an untouched task toggles
 * in one click (the autism panel's rule).
 *
 * NAVIGATION IS NOT HERE — the page's ModularStepFooter owns it, as on every
 * other panel in this module.
 */

import React, { useMemo, useState } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import StructuredNotesEditor from '../../12_observation/components/StructuredNotesEditor';
import { CYP_OBSERVATION_TAG_OPTIONS } from '../config/cypAdhdObservationConfig';

/** True when any note on the task carries text — the "ask first" signal. */
function taskHasNotes(taskData) {
  const notes = Array.isArray(taskData?.masterNotes) ? taskData.masterNotes : [];
  return notes.some((n) => typeof n?.text === 'string' && n.text.trim().length > 0);
}

export default function CypAdhdObservationTaskPanel({
  task,
  /** data.observation.tasks[task.id], or {} before the first edit. */
  taskData,
  canEdit,
  index,
  total,
  /** Intro paragraphs, shown above the card. The page passes them on the first task only. */
  intro = null,
  /** (fieldName, value) → the page writes data.observation.tasks.{id}.{fieldName}. */
  onFieldChange,
  /** The notes array for the editor (the page's localNotes overlay over masterNotes). */
  notes,
  onNotesChange,
  /** Notes-box identity and the cross-task Move catalogue (see the symptom panel). */
  notesDomainId,
  noteMoveTargets,
  onMoveNote,
}) {
  const notAdministered = Boolean(taskData?.notAdministered);
  // The flag locks content editing exactly as a read-only open does, so one
  // derived boolean guards every input below.
  const readOnly = !canEdit || notAdministered;
  // The switch itself is only live for a clinician who can edit the stage;
  // a preview viewer sees the flag but cannot flip it.
  const canToggleNotAdministered = canEdit;

  const promptsDone = useMemo(
    () => (Array.isArray(taskData?.promptsDone) ? taskData.promptsDone : []),
    [taskData?.promptsDone],
  );

  const [confirmOpen, setConfirmOpen] = useState(false);

  const writeNotAdministered = (value) => onFieldChange?.('notAdministered', value);

  const handleToggleNotAdministered = () => {
    const next = !notAdministered;
    if (next && taskHasNotes(taskData)) {
      setConfirmOpen(true);
      return;
    }
    writeNotAdministered(next);
  };

  const togglePrompt = (promptId) => {
    if (readOnly) return;
    const next = promptsDone.includes(promptId)
      ? promptsDone.filter((id) => id !== promptId)
      : [...promptsDone, promptId];
    onFieldChange?.('promptsDone', next);
  };

  return (
    <div className="bg-white p-6 rounded-lg mb-6">
      {intro && intro.length > 0 && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left space-y-2">
          {intro.map((line) => (
            <p key={line} className="text-sm text-gray-700">{line}</p>
          ))}
        </div>
      )}

      <div className="flex items-center justify-start gap-2 mb-1">
        <span className="text-gray-400 text-sm font-semibold">
          Task {index + 1} of {total}
        </span>
      </div>

      {/* Heading row: title on the left, the Not administered control pushed
          right — the autism Task Administration header. */}
      <div className="flex items-center gap-4 mb-1">
        <h2 className="text-gray-900 text-xl font-semibold leading-9 break-words text-left">
          {task.fullTitle}
        </h2>
        {canToggleNotAdministered ? (
          <label className="flex items-center gap-2 cursor-pointer select-none ml-auto">
            <span className="text-xs font-medium text-gray-500 whitespace-nowrap">Not administered</span>
            <button
              type="button"
              role="switch"
              aria-checked={notAdministered}
              aria-label="Mark this task as not administered"
              onClick={handleToggleNotAdministered}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-1 ${notAdministered ? 'bg-gray-500' : 'bg-gray-300'}`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${notAdministered ? 'translate-x-[18px]' : 'translate-x-[3px]'}`}
              />
            </button>
          </label>
        ) : notAdministered ? (
          <span className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-300">
            Not administered
          </span>
        ) : null}
      </div>
      <p className="text-gray-500 text-sm mb-4 text-left">
        <span className="font-semibold">Focus of observation: </span>
        {task.focusOfObservation}
      </p>

      {/* Why the task is greyed, and how to undo it. */}
      {notAdministered && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left">
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-gray-700">This task was not administered.</span>{' '}
            Any notes below are kept for reference but are read-only and will not
            appear on the symptom pages.
            {canToggleNotAdministered
              ? ' Switch off "Not administered" above to edit this task again.'
              : ''}
          </p>
        </div>
      )}

      <div className={`flex mb-4 ${notAdministered ? 'opacity-60' : ''}`}>
        <div className="w-full text-left">
          <div className="w-full p-6 bg-gray-50 rounded-2xl border border-gray-200 flex-col justify-start items-center gap-2 inline-flex">
            <div className="self-stretch justify-start items-start gap-2 inline-flex">
              {/* LEFT: what to do. A tick per prompt so the clinician can see
                  where they got to; probes underneath as things to watch for. */}
              <div className="w-1/2 flex flex-col gap-4">
                <div className="flex flex-col gap-3" role="group" aria-label={`${task.fullTitle} — prompts`}>
                  <label className="text-slate-700 text-sm font-semibold">
                    Prompts
                  </label>
                  {task.prompts.map((prompt) => {
                    const done = promptsDone.includes(prompt.id);
                    return (
                      <div key={prompt.id} className="flex flex-col gap-1">
                        <label className={`flex items-start gap-2 ${readOnly ? '' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            className="checkbox mt-1"
                            checked={done}
                            onChange={() => togglePrompt(prompt.id)}
                            disabled={readOnly}
                            aria-label={`Prompt done: ${prompt.label}`}
                          />
                          <span className="text-slate-700 text-sm font-medium">
                            {prompt.label}
                          </span>
                        </label>
                        {prompt.probes?.length > 0 && (
                          <ul className="list-disc pl-10 space-y-0.5">
                            {prompt.probes.map((probe) => (
                              <li key={probe} className="text-sm text-gray-600">{probe}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="border-l border-gray-200 mx-2 self-stretch"></div>

              {/* RIGHT: what the clinician records. Tagging is on — a tag
                  carries the note to that symptom's page later. */}
              <div className="w-1/2 flex-col justify-start items-start gap-2 flex">
                <label className="text-slate-700 text-sm font-semibold">
                  Clinical Notes
                </label>
                <div className="w-full">
                  <StructuredNotesEditor
                    notes={notes}
                    onChange={readOnly ? undefined : onNotesChange}
                    domainId={notesDomainId}
                    onMoveNoteIn={readOnly ? undefined : onMoveNote}
                    moveTargets={noteMoveTargets}
                    currentMoveId={notesDomainId}
                    onMoveNoteTo={readOnly ? undefined : onMoveNote}
                    placeholder="Note how the child coped, then press Enter to save each point. Use Tag to link a note to a symptom."
                    readOnlyPlaceholder={notAdministered ? 'No observation notes recorded' : undefined}
                    minHeight="12rem"
                    enableCriteriaTagging={!readOnly}
                    tagOptions={CYP_OBSERVATION_TAG_OPTIONS}
                    readOnly={readOnly}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marking a task with notes as not administered hides those notes
          from the symptom pages — worth a pause. Same gate as the autism
          Task Administration page. */}
      <AlertDialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[480px] max-w-[90vw] text-left">
            <AlertDialog.Title className="text-xl font-bold text-gray-900 mb-2">
              Mark this task as not administered?
            </AlertDialog.Title>
            <AlertDialog.Description className="text-sm text-gray-600 mb-6">
              This task has notes. They will be kept and shown greyed here, but
              they will no longer appear on the symptom pages. You can switch
              the task back on at any time.
            </AlertDialog.Description>
            <div className="flex justify-end gap-3">
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  className="font-semibold py-2 px-4 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300"
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <button
                  type="button"
                  onClick={() => writeNotAdministered(true)}
                  className="py-2 px-4 font-semibold rounded-md bg-violet-900 text-white hover:bg-violet-800"
                >
                  Mark as not administered
                </button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </div>
  );
}
